import { animate } from 'vendor';

/**
 * <specification-table>
 *
 * Wraps the row list rendered by sections/specification-table.liquid. Rows beyond "Rows revealed by
 * default" live inside a `.specification-table__reveal` wrapper that starts at height 0 (its bottom
 * border is the divider that stays visible above the toggle no matter how many rows are shown, and
 * slides down with the content as it opens). Opening/closing animates that wrapper's height between 0
 * and its natural content height, the same technique Prestige's own accordion-disclosure component uses.
 */
class SpecificationTable extends HTMLElement {
  #toggleButton;
  #reveal;
  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  connectedCallback() {
    this.#toggleButton = this.querySelector('.specification-table__toggle');
    this.#reveal = this.querySelector('.specification-table__reveal');
    if (!this.#toggleButton || !this.#reveal) return;

    this.#toggleButton.addEventListener('click', this.#toggle);
  }

  #toggle = () => {
    const expanded = this.toggleAttribute('expanded');

    this.#toggleButton.setAttribute('aria-expanded', String(expanded));
    this.#toggleButton.textContent = expanded
      ? this.#toggleButton.getAttribute('data-view-less-text')
      : this.#toggleButton.getAttribute('data-view-all-text');
    this.#reveal.toggleAttribute('inert', !expanded);

    if (expanded) {
      this.#open();
    } else {
      this.#close();
    }
  };

  #open() {
    const targetHeight = this.#reveal.scrollHeight;

    if (this.#reducedMotion) {
      this.#reveal.style.height = 'auto';
      return;
    }

    this.#reveal.style.overflow = 'hidden';

    animate(this.#reveal, { height: ['0px', `${targetHeight}px`] }, { duration: 0.25, easing: 'ease' }).finished.then(
      () => {
        this.#reveal.style.height = 'auto';
        this.#reveal.style.overflow = '';
      }
    );
  }

  #close() {
    const currentHeight = this.#reveal.scrollHeight;

    if (this.#reducedMotion) {
      this.#reveal.style.height = '';
      return;
    }

    this.#reveal.style.overflow = 'hidden';

    animate(this.#reveal, { height: [`${currentHeight}px`, '0px'] }, { duration: 0.25, easing: 'ease' }).finished.then(
      () => {
        this.#reveal.style.height = '';
        this.#reveal.style.overflow = '';
      }
    );
  }
}

if (!window.customElements.get('specification-table')) {
  window.customElements.define('specification-table', SpecificationTable);
}
